import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import Countdown from "@/components/Countdown";

const EASE = [0.16, 1, 0.3, 1];

const Stat = ({ value, suffix = "", label, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  const numeric = typeof value === "number";

  useEffect(() => {
    if (!inView || !numeric) return;
    const controls = animate(0, value, {
      duration: 1.8,
      delay: 0.9,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value, numeric]);

  return (
    <div className="stat" ref={ref} data-testid={testid}>
      <strong>{numeric ? `${display}${suffix}` : value}</strong>
      <span>{label}</span>
    </div>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textureY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const panelY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section className="hero" ref={ref} data-testid="hero-section">
      <motion.img
        className="hero-texture"
        style={{ y: textureY }}
        src="https://images.unsplash.com/photo-1649300610544-9a9a1c315d0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxkYXJRJTIwZ3JlZW4lMjBtYXJibGUlMjB0ZXh0dXJlJTIwYWJzdHJhY3QlMjBiYWNrZ3JvdW5kJTIwbHV4dXJ5fGVufDB8fHx8MTc5MjUyODIzNnww&ixlib=rb-4.1.0&q=85"
        alt=""
        aria-hidden="true"
      />
      <div className="wrap hero-grid">
        <div>
          <motion.div
            className="eyebrow"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          >
            Osogbo, Osun State · 30 January 2027
          </motion.div>
          <h1>
            <span className="line-mask">
              <motion.span
                className="line-inner"
                data-testid="hero-headline-line"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
              >
                The arena of
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span
                className="line-inner"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
              >
                <em>strategic execution.</em>
              </motion.span>
            </span>
          </h1>
          <motion.p
            className="hero-theme"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.62, ease: EASE }}
            data-testid="hero-theme-line"
          >
            Theme — “Strategies for Impact and Innovation”
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          >
            <strong>100 seats. 6 hours. 2 complex simulations.</strong> A one-day strategy
            lab for final-year students and recent graduates ready to move beyond big
            ideas and learn how to solve real problems under pressure.
          </motion.p>
          <motion.div
            className="actions"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          >
            <Link className="btn" to="/apply" data-testid="hero-apply-cta">Apply for Admission →</Link>
            <Link className="btn outline" to="/lab" data-testid="hero-learn-more">See what it is about</Link>
          </motion.div>
          <motion.p
            className="small"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
          >
            For applicants aged 20–28 who live or study in Osun State. Selection is competitive.
            <br />
            Venue:{" "}
            <a
              className="map-link"
              href="https://www.google.com/maps/search/?api=1&query=Aurora%20Conference%20Centre%2C%20Osogbo"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-venue-map-link"
            >
              Aurora Conference Centre, Osogbo ↗
            </a>
            <br />
            Follow <strong>@KOSLab</strong> · Join the conversation with <strong>#KOSLab</strong>
          </motion.p>
          <Countdown />
        </div>
        <motion.aside
          className="panel"
          style={{ y: panelY }}
          initial={{ opacity: 0, y: 50, rotate: 1.5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.6, ease: EASE }}
          data-testid="hero-panel"
        >
          <div className="eyebrow">KOSS 2027 · The core idea</div>
          <h2>
            Less talk about success.{" "}
            <span style={{ color: "var(--gold)" }}>More practice making it happen.</span>
          </h2>
          <div className="stats">
            <Stat value={100} label="Seats available" testid="hero-stat-seats" />
            <Stat value={6} suffix=" hrs" label="Focused intensive" testid="hero-stat-hours" />
            <Stat value={2} label="Strategic simulations" testid="hero-stat-simulations" />
            <Stat value="20–28" label="Age range" testid="hero-stat-age" />
          </div>
        </motion.aside>
      </div>
    </section>
  );
}
